import React, { useEffect, useState } from 'react';
import { Button, Drawer, Modal, Popconfirm, Space, Table, Typography, Card, Tooltip, Tag } from 'antd';
import { openNotification } from '../../../utils';
import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';
import dayjs from 'dayjs';
import { APP_LOCAL_DATE_FORMAT } from 'config/constants';
import { listMeasuringInstrumentCalibrations, deleteMeasuringInstrumentCalibration } from '../services/measuring-instrument-calibrationService';
import MeasuringInstrumentCalibrationForm from './Form';

export default function MeasuringInstrumentCalibrationList({ referenceId }: { referenceId?: number }){
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [previewFile, setPreviewFile] = useState<{ url: string; title: string } | null>(null);
  

  const fetchData = async () => {
    const res = await listMeasuringInstrumentCalibrations(page-1, pageSize, 'calibrationDate,desc', referenceId !== undefined ? 'measuringInstrumentInventoryId.equals=' + referenceId : '');
    setData(res.content);
    setTotal(res.totalElements);
  };
  useEffect(() => { if(referenceId !== undefined) fetchData(); }, [page, pageSize, referenceId]);
  const openCreate = () => { setEditingId(undefined); setOpen(true); };
  const openEdit = (id:number) => { setEditingId(id); setOpen(true); };
  const handleClose = () => setOpen(false);
  const handleDelete = async (id:number) => {
    try {
      await deleteMeasuringInstrumentCalibration(id);
      openNotification('success', 'Başarılı', 'Ölçüm Cihazı Kalibrasyonu Silindi');
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Ölçüm Cihazı Kalibrasyonu silme işlemi başarısız');
    }
  };
  const openPreview = (url: string, title: string) => setPreviewFile({ url, title });
  const closePreview = () => setPreviewFile(null);
  const handleDownload = () => {
    if (previewFile?.url) window.open(previewFile.url, '_blank', 'noopener,noreferrer');
  };
  const renderPreviewContent = () => {
    if (!previewFile?.url) return null;
    const lowerUrl = previewFile.url.toLowerCase();
    if (/\.(png|jpg|jpeg|gif)$/i.test(lowerUrl)) {
      return <img src={previewFile.url} alt={previewFile.title} style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }} />;
    }
    return (
      <iframe
        src={previewFile.url}
        title={previewFile.title}
        style={{ width: '100%', height: '70vh', border: 0 }}
      />
    );
  };
  const renderFileAction = (url?: string, title?: string) => {
    if (!url) return '';
    return (
      <Typography.Link href='#' onClick={e => { e.preventDefault(); openPreview(url, title || 'Dosya Önizleme'); }}>
        Görüntüle
      </Typography.Link>
    );
  };
  const columns = [
    { title: 'Kalibrasyon Numarası', dataIndex: 'trackingNumber', sorter: true },
    { title: 'Kalibrasyon Tarihi', dataIndex: 'calibrationDate', sorter: true, render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '' },
    { title: 'Kalibrasyon Geçerlilik Tarihi', dataIndex: 'nextCalibrationDate', sorter: true, render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '' },
    { title: 'Kalibrasyon Kuruluşu', dataIndex: 'calibrationOrganization', sorter: true },
    { title: 'Kalibrasyon Sertifikası', dataIndex: 'calibrationFilePath', render: (v: string) => renderFileAction(v, 'Kalibrasyon Sertifikası') },
    { title: 'Kullanım Uygunluğunun Değerlendirme Raporu', dataIndex: 'usageSuitabilityReportFilePath', render: (v: string) => renderFileAction(v, 'Kullanım Uygunluğu Raporu') },
    {
      title: 'Sonuç',
      dataIndex: 'isResultCompliant',
      sorter: true,
      render: (v: boolean) => {
        if (v === undefined || v === null) return '';
        return <Tag color={v ? 'green' : 'red'}>{v ? 'Uygun' : 'Uygun Değil'}</Tag>;
      }
    },
    { title: 'İşlemler', render: (_:any, r:any) => (
      <Space>
        <Tooltip title='Düzenle'>
          <Button
            icon={<img src={PenIcon} alt="pen" className="icon" />}
            shape="circle"
            onClick={() => openEdit(r.id)}
            className="icon-button"
          />
        </Tooltip>
        <Popconfirm title='Sil?' onConfirm={() => handleDelete(r.id)}>
          <Tooltip title='Sil'>
            <Button
              icon={<img src={TrashIcon} alt="trash" className="icon" />}
              shape="circle"
              className="icon-button ml-12px"
            />
          </Tooltip>
        </Popconfirm>
      </Space>
    ) }
  ];
  return (
    <>
      
      <Card
        className='br-30px'
        title={<Typography.Title level={4} style={{ margin: 0 }}>Ölçüm Cihazı Kalibrasyonu</Typography.Title>}
        extra={<Button type='primary' onClick={openCreate}>Ekle</Button>}
      >
        <Table
          rowKey='id'
          dataSource={data}
          columns={columns}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['50', '100', '200', '500'],
            onChange:(p,s)=>{setPage(p); setPageSize(s);}
          }}
        />
      </Card>
      <Drawer open={open} onClose={handleClose} destroyOnClose width={480} title={editingId ? 'Ölçüm Cihazı Kalibrasyonu Düzenle' : 'Kalibrasyon Bilgilerini Ekle'}>
        <MeasuringInstrumentCalibrationForm id={editingId} onSuccess={() => { handleClose(); fetchData();}} referenceId={referenceId} />
      </Drawer>
      <Modal
        open={!!previewFile}
        onCancel={closePreview}
        width={900}
        title={previewFile?.title || 'Dosya Önizleme'}
        footer={[
          <Button key='close' onClick={closePreview}>Kapat</Button>,
          <Button key='download' type='primary' onClick={handleDownload}>İndir</Button>
        ]}
      >
        {renderPreviewContent()}
      </Modal>
    </>
  );
}
